import * as ww from "@wecom/jssdk";
import wecomJssdkPackage from "@wecom/jssdk/package.json";
import type { JssdkSignatureResponse } from "../shared/types";
import {
  classifyGetCurExternalContactError,
  normalizeWeComJsApiError,
  shouldFallbackToRawInvoke,
  type GetExternalContactClassification,
  type NormalizedWeComJsApiError
} from "./wecom-jsapi-error";

interface GetCurExternalContactRawResult {
  userId?: string;
  errMsg?: string;
  err_msg?: string;
  errCode?: number | string;
  err_code?: number | string;
  code?: number | string;
  [key: string]: unknown;
}

export interface GetCurExternalContactDiagnostic {
  called: true;
  sdkMethod: "ww.getCurExternalContact";
  success: boolean;
  userId: string | null;
  errMsg: string | null;
  errCode: number | string | null;
  rawResult: Record<string, unknown>;
  classification: GetExternalContactClassification;
  highLevelResult: Record<string, unknown> | null;
  rawInvokeResult: Record<string, unknown> | null;
  calledAt: string;
}

export interface WeComJssdkMetadata {
  sdkVersion: string;
  packageVersion: string;
}

export function isWeComBrowser(): boolean {
  return ww.env.isWeCom || /wxwork/i.test(window.navigator.userAgent);
}

export async function registerWeComJssdk(
  getSignature: (url: string) => Promise<JssdkSignatureResponse>,
  onFailure: (error: Error | unknown) => void
): Promise<void> {
  const initialSignature = await getSignature(window.location.href);

  ww.register({
    corpId: initialSignature.corpId,
    agentId: initialSignature.agentId,
    jsApiList: ["getCurExternalContact"],
    getConfigSignature: async (url) => {
      const signature = await getSignature(url);
      return signature.config;
    },
    getAgentConfigSignature: async (url) => {
      const signature = await getSignature(url);
      return signature.agentConfig;
    },
    onConfigFail: onFailure,
    onAgentConfigFail: onFailure
  });

  await ww.ensureCorpConfigReady();
  await ww.ensureAgentConfigReady();
}

export async function getCurrentExternalUserId(): Promise<string> {
  const result = await ww.getCurExternalContact();
  return result.userId;
}

export async function getCurrentExternalContactDiagnostic(): Promise<GetCurExternalContactDiagnostic> {
  const calledAt = new Date().toISOString();

  try {
    const highLevelResult = await ww.getCurExternalContact();
    return {
      called: true,
      sdkMethod: "ww.getCurExternalContact",
      success: true,
      userId: highLevelResult.userId,
      errMsg: null,
      errCode: null,
      rawResult: normalizeWeComJsApiError(highLevelResult).raw,
      classification: "unknown",
      highLevelResult: normalizeWeComJsApiError(highLevelResult).raw,
      rawInvokeResult: null,
      calledAt
    };
  } catch (error) {
    const highLevelError = normalizeWeComJsApiError(error);
    if (shouldFallbackToRawInvoke(highLevelError)) {
      return getRawInvokeDiagnostic(calledAt, highLevelError);
    }

    return createFailedDiagnostic(calledAt, highLevelError, highLevelError.raw, null);
  }
}

export function getWeComJssdkMetadata(): WeComJssdkMetadata {
  return {
    sdkVersion: ww.SDK_VERSION,
    packageVersion: String((wecomJssdkPackage as { version?: string }).version ?? ww.SDK_VERSION)
  };
}

async function getRawInvokeDiagnostic(
  calledAt: string,
  highLevelError: NormalizedWeComJsApiError
): Promise<GetCurExternalContactDiagnostic> {
  try {
    const rawResult = await ww.invoke<GetCurExternalContactRawResult>("getCurExternalContact", {});
    const normalizedRaw = normalizeWeComJsApiError(rawResult);
    if (rawResult.userId) {
      return {
        called: true,
        sdkMethod: "ww.getCurExternalContact",
        success: true,
        userId: rawResult.userId,
        errMsg: normalizedRaw.errMsg,
        errCode: normalizedRaw.errCode,
        rawResult: normalizedRaw.raw,
        classification: "unknown",
        highLevelResult: highLevelError.raw,
        rawInvokeResult: normalizedRaw.raw,
        calledAt
      };
    }

    return createFailedDiagnostic(calledAt, normalizedRaw, highLevelError.raw, normalizedRaw.raw);
  } catch (rawInvokeError) {
    const normalizedRawError = normalizeWeComJsApiError(rawInvokeError);
    return createFailedDiagnostic(calledAt, normalizedRawError, highLevelError.raw, normalizedRawError.raw);
  }
}

function createFailedDiagnostic(
  calledAt: string,
  normalizedError: NormalizedWeComJsApiError,
  highLevelResult: Record<string, unknown> | null,
  rawInvokeResult: Record<string, unknown> | null
): GetCurExternalContactDiagnostic {
  return {
    called: true,
    sdkMethod: "ww.getCurExternalContact",
    success: false,
    userId: null,
    errMsg: normalizedError.errMsg,
    errCode: normalizedError.errCode,
    rawResult: normalizedError.raw,
    classification: classifyGetCurExternalContactError(normalizedError),
    highLevelResult,
    rawInvokeResult,
    calledAt
  };
}
